// Lab 1 safety invariants: at-most-once application effects, reply
// authenticity, retry identity, and result correspondence.
import { MSG_REQUEST, MSG_REPLY } from './model';

function requests(state) {
  return state.events.filter(
    (e) => e.kind === 'message' && e.payload && e.payload.msgType === MSG_REQUEST
  );
}

function replies(state) {
  return state.events.filter(
    (e) => e.kind === 'message' && e.payload && e.payload.msgType === MSG_REPLY
  );
}

export function atMostOnceExecution(state) {
  const node = state.nodes[state.config.server];
  if (!node) return null;
  const seen = new Set();
  for (const id of node.durable.appliedIds) {
    if (seen.has(id)) {
      return {
        invariant: 'lab1-at-most-once',
        message: 'command ' + id + ' applied more than once',
      };
    }
    seen.add(id);
  }
  return null;
}

export function replyAuthenticity(state) {
  const sent = new Set();
  state.config.clients.forEach((c) => {
    const node = state.nodes[c];
    if (node) node.durable.sentIds.forEach((id) => sent.add(id));
  });
  const bad = replies(state).find(
    (e) => !sent.has(e.payload.client + '#' + e.payload.seq)
  );
  if (bad) {
    return {
      invariant: 'lab1-reply-authenticity',
      message:
        'reply ' + bad.payload.client + '#' + bad.payload.seq + ' answers an unknown call',
    };
  }
  return null;
}

export function retryPreservesIdentity(state) {
  for (const c of state.config.clients) {
    const node = state.nodes[c];
    if (!node) continue;
    const distinct = new Set(
      requests(state)
        .filter((e) => e.payload.client === c)
        .map((e) => e.payload.client + '#' + e.payload.seq)
    );
    if (distinct.size > node.durable.accepted) {
      return {
        invariant: 'lab1-retry-identity',
        message:
          'client ' + c + ' shows ' + distinct.size + ' identities for ' + node.durable.accepted + ' calls',
      };
    }
  }
  return null;
}

export function resultCorrespondence(state) {
  const server = state.nodes[state.config.server];
  if (!server) return null;
  const applied = new Set(server.durable.appliedIds);
  for (const c of state.config.clients) {
    const node = state.nodes[c];
    if (!node) continue;
    const orphan = node.durable.completed.find((r) => !applied.has(c + '#' + r.seq));
    if (orphan) {
      return {
        invariant: 'lab1-result-correspondence',
        message: 'client ' + c + ' completed seq ' + orphan.seq + ' the server never applied',
      };
    }
  }
  return null;
}

export function checkLab1Invariants(state) {
  return [
    atMostOnceExecution,
    replyAuthenticity,
    retryPreservesIdentity,
    resultCorrespondence,
  ]
    .map((fn) => fn(state))
    .filter(Boolean);
}
